import * as React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { Dialog, DialogContent } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Field, Input, NativeSelect, Textarea } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/misc'
import {
  ENTITY_TYPES,
  TASK_PRIORITIES,
  TASK_STATUSES,
  TasksA,
  useCreateTask,
  useUpdateTask,
  type Task,
  type TaskCreateInput,
  type TaskUpdateInput,
} from '@/api/tasks'
import { useStaffOptions } from '@/api/users'
import { useMe } from '@/api/auth'
import { useT, type TKey } from '@/i18n'
import { fromKyivInput, toKyivInput } from '@/lib/dates'
import { applyFieldErrors } from '@/lib/utils'
import { PRIORITY_LABEL, STATUS_LABEL } from './taskUtils'
import { useGo } from '@/lib/routing'

const schema = z.object({
  title: z.string().trim().min(1, 'required').max(500, 'max500'),
  description: z.string().max(10000, 'max10000'),
  priority: z.enum(TASK_PRIORITIES),
  status: z.enum(TASK_STATUSES),
  assignee_id: z.string(),
  due_at: z.string(),
  visibility: z.enum(['team', 'private']),
  entity_type: z.string(),
  entity_id: z.string(),
  watchers: z.array(z.string()),
})
export type TaskFormValues = z.infer<typeof schema>

const FIELDS = ['title', 'description', 'priority', 'status', 'assignee_id', 'due_at', 'visibility', 'entity_type', 'entity_id'] as const

export interface TaskFormDialogProps {
  open: boolean
  onOpenChange: (v: boolean) => void
  task?: Task
  defaults?: Partial<Pick<TaskFormValues, 'title' | 'description' | 'entity_type' | 'entity_id' | 'due_at' | 'status'>>
  onSaved?: (t: Task) => void
}

export function toCreateInput(v: TaskFormValues): TaskCreateInput {
  return {
    title: v.title.trim(),
    description: v.description || undefined,
    priority: v.priority,
    status: v.status,
    assignee_id: v.assignee_id || undefined,
    due_at: v.due_at ? fromKyivInput(v.due_at) : undefined,
    visibility: v.visibility,
    entity_type: v.entity_type && v.entity_id ? v.entity_type : undefined,
    entity_id: v.entity_type && v.entity_id ? v.entity_id.trim() : undefined,
    watchers: v.watchers.length ? v.watchers : undefined,
  }
}

export function toUpdateInput(task: Task, v: TaskFormValues): TaskUpdateInput {
  const out: TaskUpdateInput = { task_id: task.id }
  if (v.title.trim() !== task.title) out.title = v.title.trim()
  if (v.description !== task.description) out.description = v.description
  if (v.priority !== task.priority) out.priority = v.priority
  if (v.status !== task.status) out.status = v.status
  if (v.assignee_id !== (task.assignee_id ?? '')) out.assignee_id = v.assignee_id
  if (v.visibility !== task.visibility) out.visibility = v.visibility
  const due = v.due_at ? fromKyivInput(v.due_at) : undefined
  if (!due && task.due_at) out.clear_due = true
  else if (due && (!task.due_at || new Date(due).getTime() !== new Date(task.due_at).getTime())) out.due_at = due
  return out
}

const ENTITY_LABEL: Record<string, TKey> = {
  client: 'entity.client',
  pet: 'entity.pet',
  visit: 'entity.visit',
  journey: 'entity.journey',
  call: 'entity.call',
  rating: 'entity.rating',
  insight: 'entity.insight',
}

export function TaskFormDialog({ open, onOpenChange, task, defaults, onSaved }: TaskFormDialogProps) {
  const t = useT()
  const me = useMe().data
  const staff = useStaffOptions()
  const create = useCreateTask()
  const update = useUpdateTask()
  const go = useGo()
  const editing = !!task

  const form = useForm<TaskFormValues>({
    resolver: zodResolver(schema),
    defaultValues: task
      ? {
          title: task.title,
          description: task.description,
          priority: task.priority,
          status: task.status,
          assignee_id: task.assignee_id ?? '',
          due_at: toKyivInput(task.due_at),
          visibility: task.visibility,
          entity_type: task.entity_type,
          entity_id: task.entity_id,
          watchers: [],
        }
      : {
          title: defaults?.title ?? '',
          description: defaults?.description ?? '',
          priority: 'medium',
          status: defaults?.status ?? 'todo',
          assignee_id: me?.user_id ?? '',
          due_at: defaults?.due_at ?? '',
          visibility: 'team',
          entity_type: defaults?.entity_type ?? '',
          entity_id: defaults?.entity_id ?? '',
          watchers: [],
        },
  })
  const { register, handleSubmit, formState, setError, watch, setValue } = form
  const watchers = watch('watchers')

  const onSubmit = handleSubmit(async (v) => {
    try {
      let saved: Task
      if (task) {
        saved = await update.mutateAsync(toUpdateInput(task, v))
        toast.success(t('tasks.saved'))
      } else {
        saved = await create.mutateAsync(toCreateInput(v))
        toast.success(t('tasks.created'), {
          action: { label: t('common.open'), onClick: () => go(`/tasks?task=${saved.id}`) },
        })
      }
      onSaved?.(saved)
      onOpenChange(false)
    } catch (e) {
      applyFieldErrors(e, setError, FIELDS, (_f, msg) => (msg === 'required' ? t('validation.required') : msg))
    }
  })

  const err = (k: keyof TaskFormValues) => {
    const m = formState.errors[k]?.message as string | undefined
    if (!m) return undefined
    if (m === 'required') return t('validation.required')
    if (m === 'max500') return t('validation.max', { n: 500 })
    if (m === 'max10000') return t('validation.max', { n: 10000 })
    return m
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title={editing ? t('tasks.edit') : t('tasks.new')}
        size="lg"
        footer={
          <>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" form="task-form" loading={formState.isSubmitting} actionId={editing ? TasksA.update : TasksA.create} data-testid="task-submit">
              {editing ? t('common.save') : t('tasks.create')}
            </Button>
          </>
        }
      >
        <form id="task-form" onSubmit={onSubmit} noValidate className="grid gap-4 sm:grid-cols-2">
          <Field label={t('tasks.field.title')} htmlFor="task-title" error={err('title')} className="sm:col-span-2" required>
            <Input id="task-title" autoFocus aria-invalid={!!formState.errors.title} placeholder={t('tasks.titlePlaceholder')} {...register('title')} />
          </Field>
          <Field label={t('tasks.field.description')} htmlFor="task-desc" error={err('description')} className="sm:col-span-2">
            <Textarea id="task-desc" rows={4} {...register('description')} />
          </Field>
          <Field label={t('tasks.field.priority')} htmlFor="task-priority">
            <NativeSelect id="task-priority" {...register('priority')}>
              {TASK_PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {t(PRIORITY_LABEL[p])}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label={t('tasks.field.status')} htmlFor="task-status">
            <NativeSelect id="task-status" {...register('status')}>
              {TASK_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(STATUS_LABEL[s])}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <Field label={t('tasks.field.assignee')} htmlFor="task-assignee">
            <NativeSelect id="task-assignee" {...register('assignee_id')}>
              <option value="">{t('tasks.unassigned')}</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                  {s.id === me?.user_id ? ` (${t('common.me')})` : ''}
                </option>
              ))}
              {task?.assignee_id && !staff.some((s) => s.id === task.assignee_id) && <option value={task.assignee_id}>{task.assignee_name}</option>}
            </NativeSelect>
          </Field>
          <Field label={t('tasks.field.due')} htmlFor="task-due" hint={t('tasks.kyivTime')} error={err('due_at')}>
            <Input id="task-due" type="datetime-local" {...register('due_at')} />
          </Field>
          <Field label={t('tasks.field.visibility')} htmlFor="task-visibility">
            <NativeSelect id="task-visibility" {...register('visibility')}>
              <option value="team">{t('tasks.visibility.team')}</option>
              <option value="private">{t('tasks.visibility.private')}</option>
            </NativeSelect>
          </Field>
          {!editing && (
            <div className="grid grid-cols-[1fr_1.4fr] gap-2">
              <Field label={t('tasks.field.link')} htmlFor="task-entity-type">
                <NativeSelect id="task-entity-type" {...register('entity_type')}>
                  <option value="">—</option>
                  {ENTITY_TYPES.map((e) => (
                    <option key={e} value={e}>
                      {t(ENTITY_LABEL[e])}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
              <Field label={t('tasks.field.linkId')} htmlFor="task-entity-id">
                <Input id="task-entity-id" disabled={!watch('entity_type')} {...register('entity_id')} />
              </Field>
            </div>
          )}
          {!editing && staff.length > 1 && (
            <fieldset className="sm:col-span-2">
              <legend className="mb-1.5 text-[13px] font-medium">{t('tasks.field.watchers')}</legend>
              <div className="flex max-h-28 flex-wrap gap-x-4 gap-y-2 overflow-y-auto">
                {staff
                  .filter((s) => s.id !== me?.user_id)
                  .map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-[13px]">
                      <Checkbox
                        checked={watchers.includes(s.id)}
                        onCheckedChange={(c) =>
                          setValue(
                            'watchers',
                            c ? [...watchers, s.id] : watchers.filter((x) => x !== s.id),
                          )
                        }
                      />
                      {s.name}
                    </label>
                  ))}
              </div>
            </fieldset>
          )}
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function useTaskDialog() {
  const [state, setState] = React.useState<{ open: boolean; task?: Task; defaults?: TaskFormDialogProps['defaults'] }>({ open: false })
  return {
    ...state,
    openCreate: (defaults?: TaskFormDialogProps['defaults']) => setState({ open: true, defaults }),
    openEdit: (task: Task) => setState({ open: true, task }),
    setOpen: (open: boolean) => setState((s) => ({ ...s, open })),
  }
}
